import { proxy } from 'valtio'
import { derive, devtools } from 'valtio/utils'
import { type ApiCallType } from '../types/ApiCallType'
import { resetProxy } from '../utils/proxyUtils'
import { FormFieldNamesEnum } from '../../routes/app/settings/account/ChangePasswordForm/formFieldNamesEnum'

export const FIELDS_REQUIRED_FOR_SUBMISSION = [
  FormFieldNamesEnum.Password,
  FormFieldNamesEnum.PasswordConfirmation
]

interface ChangePasswordProxy {
  allowSubmit: boolean
  apiCall: ApiCallType
  completed: boolean
  formValues: { [key in FormFieldNamesEnum]?: string | null }
  formErrors: { [key in FormFieldNamesEnum]?: string | null }
  formDirtied: readonly FormFieldNamesEnum[]
  formBlurred: readonly FormFieldNamesEnum[]
  showPasswordError: boolean
  showPasswordConfirmationError: boolean
}

const initialValues = {
  apiCall: {
    error: null,
    loading: false
  },
  completed: false,
  formBlurred: [],
  formDirtied: [],
  formErrors: {},
  formValues: {}
}

const baseProxy = proxy(initialValues)

const showErrorFunctionGenerator = (fieldName: FormFieldNamesEnum) => (get: any) => {
  const { formDirtied, formBlurred, formErrors } = get(baseProxy)
  const isDirty = (formDirtied as string[]).includes(fieldName)
  const isBlurred = (formBlurred as string[]).includes(fieldName)
  const isErrored = Boolean((formErrors as { [key in FormFieldNamesEnum]?: string | null })[fieldName])
  return isDirty && isBlurred && isErrored
}

export const proxyChangePassword = derive({
  allowSubmit: (get) => {
    const { apiCall: { loading }, formDirtied, formErrors, formValues } = get(baseProxy)
    const noErrors = Object.values(formErrors).filter(x => x).length === 0
    // @ts-expect-error
    const allDirtied = FIELDS_REQUIRED_FOR_SUBMISSION.every((x: string) => formDirtied.includes(x))
    // @ts-expect-error
    const passwordsMatch = formValues[FormFieldNamesEnum.Password] as string === formValues[FormFieldNamesEnum.PasswordConfirmation] as string
    return noErrors && allDirtied && passwordsMatch && !loading
  },
  showPasswordConfirmationError: showErrorFunctionGenerator(FormFieldNamesEnum.PasswordConfirmation),
  showPasswordError: showErrorFunctionGenerator(FormFieldNamesEnum.Password)
}, { proxy: baseProxy }) as ChangePasswordProxy

devtools(proxyChangePassword, { name: 'proxyChangePassword' })

export function resetProxyChangePassword (): void {
  resetProxy(initialValues, proxyChangePassword)
}
